/* eslint-disable-next-line local/require-storybook-story --
 * useInboxEcgViewer.createData HTTP-downloads the strip on mount — ranged arraybuffer GETs against
 * a signed data URL (entities/study/lib/ecgDataHandler.ts, downloadSelectedEcgData) — and hands the
 * deinterleaved samples to the fabricJS ECGViewer below (see that file's own disable comment).
 * Offline the isLoadedData/dataSamples guard never flips, so a story could only ever render the
 * skeleton shell, never the view itself. Covered by E2E/manual verification, not Storybook. */
import { backIcon } from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useInboxEcgViewer } from './useInboxEcgViewer';
import { generateTitleBlockArray } from './handler';
import ECGClient from '../ecgClient';
import ECGViewer from '../ecgViewer/ecgViewer';
import './inboxEcgViewer.scss';
import type { InboxECGViewerProps } from './inboxEcgViewer.types';

/**
 * Inbox report ECG view that pairs a back button and study title blocks with the ECG chart of the selected inbox item. Pulls state, titleInfo and onClickBack from useInboxEcgViewer, maps generateTitleBlockArray over the loaded title info, and renders ECGViewer inside ECGClient once samples exist, falling back to skeleton placeholders for the title and chart.
 *
 * @param data - Inbox item supplying the study start date, facility country and timezone.
 * @param handleClickBack - Callback invoked when the back button is pressed.
 * @returns The rendered inbox ECG viewer element.
 */
const InboxECGViewer = ({ data, handleClickBack }: InboxECGViewerProps) => {
  const { state, titleInfo, onClickBack } = useInboxEcgViewer({ data, handleClickBack });

  return (
    <div className="inbox-ecg-viewer">
      <div className="inbox-ecg-viewer__title">
        <CustomButton
          style="neutral"
          type="outline"
          size="small"
          id="back-inbox-ecg-btn"
          icon={<ReactSVG src={backIcon} />}
          onClick={onClickBack}
          ariaLabel={t`Back`}
        />

        {!_.isEmpty(titleInfo.current) ? (
          <div className="title-block-container">
            {_.map(generateTitleBlockArray(titleInfo.current), (element, index) => (
              <div key={index} className="title-block">
                <div className="title-block__title">{element.title}</div>

                <div className="flex items-center">
                  <div className="title-block__content">{element.content}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="title-skeleton-container">
            <Skeleton height="100%" />
          </div>
        )}
      </div>

      <div className="inbox-ecg-viewer__body">
        {state.isLoadedData && !!state.ecgObject.dataSamples?.length ? (
          <ECGClient>
            <ECGViewer
              isLoadedData={state.isLoadedData}
              ecg={state.ecgObject}
              configChart={state.configChart}
              startStudyDate={data.startStudyDate}
              facilityCountry={data.facilityCountry}
              facilityTimezone={data.facilityTimezone}
              timeObject={state.timeObject}
            />
          </ECGClient>
        ) : (
          <div className="ecg-viewer-skeleton-container">
            <div className="chart-container">
              <Skeleton height="100%" />
            </div>

            <div className="menu-container">
              <Skeleton height="100%" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxECGViewer;
